import React, { useState, Children } from 'react';
import { TagIcon, PlusIcon, CalendarIcon, EditIcon, TrashIcon, CheckCircleIcon, ClockIcon, EyeIcon, MousePointerClickIcon, MegaphoneIcon, SearchIcon, PercentIcon, CopyIcon, MoreHorizontalIcon, PauseIcon, FilterIcon, UsersIcon, HeartIcon } from 'lucide-react';
import { useDarkMode } from '../../../context/DarkModeContext';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { useRoleTheme } from '../../../hooks/useRoleTheme';
import { useNavigate, Link } from 'react-router-dom';
import BackButton from '../../../components/UI/BackButton';
// Unified Type Definition
type PromotionItem = {
  id: number;
  type: 'promotion' | 'discount_code' | 'anthology_donation';
  title: string;
  subtitle?: string;
  status: 'active' | 'scheduled' | 'expired' | 'draft';
  startDate: string;
  endDate: string;
  stats: {
    views?: number;
    clicks?: number;
    uses?: number;
    revenue?: string;
  };
  details: {
    discountType: 'percentage' | 'fixed';
    value: number | string;
    code?: string;
    platform?: string;
  };
  // Anthology donation fields
  nonprofitName?: string;
  donationPercentage?: number;
  donationScope?: string;
  totalDonated?: string;
};
// Mock Data (Combined)
const allItems: PromotionItem[] = [{
  id: 1,
  type: 'promotion',
  title: 'The Midnight Garden',
  subtitle: 'by Sarah Chen',
  status: 'active',
  startDate: '2026-02-01',
  endDate: '2026-02-28',
  stats: {
    views: 1240,
    clicks: 145
  },
  details: {
    discountType: 'percentage',
    value: 20,
    platform: 'Bookbub'
  }
}, {
  id: 2,
  type: 'discount_code',
  title: 'WELCOME20',
  subtitle: '20% OFF Storewide',
  status: 'active',
  startDate: '2026-10-01',
  endDate: '2026-10-31',
  stats: {
    uses: 45,
    revenue: '$850'
  },
  details: {
    discountType: 'percentage',
    value: 20,
    code: 'WELCOME20'
  }
}, {
  id: 3,
  type: 'promotion',
  title: 'Echoes of Tomorrow',
  subtitle: 'by Marcus Webb',
  status: 'scheduled',
  startDate: '2026-03-01',
  endDate: '2026-03-07',
  stats: {
    views: 0,
    clicks: 0
  },
  details: {
    discountType: 'fixed',
    value: 2.99,
    platform: 'Self-run'
  }
}, {
  id: 4,
  type: 'discount_code',
  title: 'SPOOKY5',
  subtitle: '$5.00 OFF Orders > $20',
  status: 'active',
  startDate: '2026-10-15',
  endDate: '2026-11-01',
  stats: {
    uses: 89,
    revenue: '$1,200'
  },
  details: {
    discountType: 'fixed',
    value: 5,
    code: 'SPOOKY5'
  }
}, {
  id: 5,
  type: 'promotion',
  title: 'Shadows & Starlight',
  subtitle: 'by Elena Vasquez',
  status: 'expired',
  startDate: '2026-01-01',
  endDate: '2026-01-15',
  stats: {
    views: 3200,
    clicks: 89
  },
  details: {
    discountType: 'percentage',
    value: 50,
    platform: 'StoryOrigin'
  }
}, {
  id: 6,
  type: 'discount_code',
  title: 'SUMMERREADS',
  subtitle: '10% OFF Summer Collection',
  status: 'expired',
  startDate: '2026-06-01',
  endDate: '2026-08-31',
  stats: {
    uses: 156,
    revenue: '$2,100'
  },
  details: {
    discountType: 'percentage',
    value: 10,
    code: 'SUMMERREADS'
  }
},
// Anthology Donation items
{
  id: 7,
  type: 'anthology_donation',
  title: 'Hearts for Hope',
  subtitle: "Donating to Children's Literacy Foundation",
  status: 'active',
  startDate: '2026-01-01',
  endDate: '',
  stats: {
    revenue: '$2,450'
  },
  details: {
    discountType: 'percentage',
    value: 25
  },
  nonprofitName: "Children's Literacy Foundation",
  donationPercentage: 25,
  donationScope: 'This Anthology',
  totalDonated: '$2,450'
}, {
  id: 8,
  type: 'anthology_donation',
  title: 'All Store Sales',
  subtitle: 'Donating to Rainbow Reads Initiative',
  status: 'active',
  startDate: '2026-06-01',
  endDate: '2026-12-31',
  stats: {
    revenue: '$890'
  },
  details: {
    discountType: 'percentage',
    value: 10
  },
  nonprofitName: 'Rainbow Reads Initiative',
  donationPercentage: 10,
  donationScope: 'All Sales',
  totalDonated: '$890'
}, {
  id: 9,
  type: 'anthology_donation',
  title: 'Midnight Tales for Change',
  subtitle: 'Donating to Mental Health Through Stories',
  status: 'expired',
  startDate: '2025-10-01',
  endDate: '2025-12-31',
  stats: {
    revenue: '$1,200'
  },
  details: {
    discountType: 'percentage',
    value: 15
  },
  nonprofitName: 'Mental Health Through Stories',
  donationPercentage: 15,
  donationScope: 'This Anthology',
  totalDonated: '$1,200'
}];
const PromotionsDiscounts = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'all' | 'promotions' | 'discounts' | 'donations' | 'expired'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Filter Logic
  const filteredItems = allItems.filter(item => {
    // Tab Filter
    if (activeTab === 'promotions' && item.type !== 'promotion') return false;
    if (activeTab === 'discounts' && item.type !== 'discount_code') return false;
    if (activeTab === 'donations' && item.type !== 'anthology_donation') return false;
    if (activeTab === 'expired' && item.status !== 'expired') return false;
    // Hide expired from 'all' view by default, but show anthology donations
    if (activeTab === 'all' && item.status === 'expired') return false;
    // Search Filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return item.title.toLowerCase().includes(query) || item.subtitle?.toLowerCase().includes(query) || item.details.code?.toLowerCase().includes(query) || item.nonprofitName?.toLowerCase().includes(query);
    }
    return true;
  });
  // Stats Calculation
  const activePromos = allItems.filter(i => i.type === 'promotion' && i.status === 'active').length;
  const activeCodes = allItems.filter(i => i.type === 'discount_code' && i.status === 'active').length;
  const activeDonations = allItems.filter(i => i.type === 'anthology_donation' && i.status === 'active').length;
  const totalRevenue = '$4,150';
  // Helper: Date Formatter
  const formatDateRange = (start: string, end: string) => {
    if (!end) {
      const s = new Date(start);
      const options: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      };
      return `${s.toLocaleDateString('en-US', options)} – Ongoing`;
    }
    const s = new Date(start);
    const e = new Date(end);
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric'
    };
    return `${s.toLocaleDateString('en-US', options)} – ${e.toLocaleDateString('en-US', {
      ...options,
      year: 'numeric'
    })}`;
  };
  return <div className={`min-h-screen py-8 ${dk.page}`} data-id="element-19785" data-mp="pygkobfb4m9n">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-19786" data-mp="l42tymblb3bb">
        <div className="mb-6" data-id="element-19787" data-mp="g6klsy5ecs4d">
          <BackButton data-id="element-19788" data-mp="g5j6ghhcljgu" />
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8" data-id="element-19789" data-mp="c4fsk0iv1xi9">
          <div data-id="element-19790" data-mp="75mdw4ru80gi">
            <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-19791" data-mp="7536hhztcugq">
              Promotions & Discounts
            </h1>
            <p className={`mt-2 ${dk.textSecondary}`} data-id="element-19792" data-mp="27ks3c311n1c">
              Manage your sales, deals, coupon codes, and charity donations in
              one place.
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex gap-3" data-id="element-19793" data-mp="1wksubotc10x0">
            <Link to="/vendors/manage?type=sale" className={`inline-flex items-center px-4 py-2 rounded-lg text-white font-medium transition-colors ${rt.bgDark} hover:opacity-90`} data-id="element-19794" data-mp="1wi6mrp6fyles">
              <PlusIcon className="h-5 w-5 mr-2" data-id="element-19795" data-mp="15ex6w5igdbmg" />
              Create New
            </Link>
          </div>
        </div>

        {/* Stats Row - Now 4 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-19796" data-mp="rlpggh63ym6c">
          <div className={`p-5 rounded-xl shadow-sm border ${dk.card}`} data-id="element-19797" data-mp="vnu8jbgwraqv">
            <div className="flex items-center justify-between" data-id="element-19798" data-mp="hokryaosiemd">
              <div data-id="element-19799" data-mp="mopae699djf8">
                <p className={`text-sm font-medium ${dk.textMuted}`} data-id="element-19800" data-mp="mp8jcxgxkxh4">
                  Active Promotions
                </p>
                <p className={`text-2xl font-bold mt-1 ${dk.textHeading}`} data-id="element-19801" data-mp="8q924avqzkuz">
                  {activePromos}
                </p>
              </div>
              <div className={`p-3 rounded-full ${darkMode ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-600'}`} data-id="element-19802" data-mp="232yxkx1rl24h">
                <MegaphoneIcon className="h-6 w-6" data-id="element-19803" data-mp="273r7ztmdhd2c" />
              </div>
            </div>
          </div>
          <div className={`p-5 rounded-xl shadow-sm border ${dk.card}`} data-id="element-19804" data-mp="3rmuoxxoumia1">
            <div className="flex items-center justify-between" data-id="element-19805" data-mp="3nmas6v86tgof">
              <div data-id="element-19806" data-mp="56qjilcrq2ag">
                <p className={`text-sm font-medium ${dk.textMuted}`} data-id="element-19807" data-mp="581w9ao6ykdp">
                  Active Discount Codes
                </p>
                <p className={`text-2xl font-bold mt-1 ${dk.textHeading}`} data-id="element-19808" data-mp="eyzkto4auta">
                  {activeCodes}
                </p>
              </div>
              <div className={`p-3 rounded-full ${darkMode ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`} data-id="element-19809" data-mp="a6d2lbfa2cr6">
                <TagIcon className="h-6 w-6" data-id="element-19810" data-mp="o5302s60eeg2" />
              </div>
            </div>
          </div>
          <div className={`p-5 rounded-xl shadow-sm border ${dk.card}`} data-id="element-19811" data-mp="j7kosv4irn4w">
            <div className="flex items-center justify-between" data-id="element-19812" data-mp="2py0o8aouciuo">
              <div data-id="element-19813" data-mp="2kyeewcc4vmr5">
                <p className={`text-sm font-medium ${dk.textMuted}`} data-id="element-19814" data-mp="2kz7mffix2gac">
                  Active Donations
                </p>
                <p className={`text-2xl font-bold mt-1 ${dk.textHeading}`} data-id="element-19815" data-mp="3e7ovfuwhuly9">
                  {activeDonations}
                </p>
              </div>
              <div className={`p-3 rounded-full ${darkMode ? 'bg-rose-900/20 text-rose-400' : 'bg-rose-100 text-rose-600'}`} data-id="element-19816" data-mp="3555y1qbh91je">
                <HeartIcon className="h-6 w-6" data-id="element-19817" data-mp="30871700fighi" />
              </div>
            </div>
          </div>
          <div className={`p-5 rounded-xl shadow-sm border ${dk.card}`} data-id="element-19818" data-mp="qfwlvrddi2i">
            <div className="flex items-center justify-between" data-id="element-19819" data-mp="3swbadvi7lboy">
              <div data-id="element-19820" data-mp="3o41lmistyho9">
                <p className={`text-sm font-medium ${dk.textMuted}`} data-id="element-19821" data-mp="3o39r7bayypzk">
                  Total Revenue Impact
                </p>
                <p className={`text-2xl font-bold mt-1 ${dk.textHeading}`} data-id="element-19822" data-mp="3j4yhd0kocfhc">
                  {totalRevenue}
                </p>
              </div>
              <div className={`p-3 rounded-full ${darkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600'}`} data-id="element-19823" data-mp="fnyfwila332k">
                <ClockIcon className="h-6 w-6" data-id="element-19824" data-mp="n1qzmrbcrdj5" />
              </div>
            </div>
          </div>
        </div>

        {/* Controls: Search & Tabs */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6" data-id="element-19825" data-mp="wxkguo00nv79">
          {/* Tabs */}
          <div className="flex p-1 space-x-1 rounded-xl bg-gray-100 dark:bg-gray-800 w-full md:w-auto overflow-x-auto" data-id="element-19826" data-mp="11072u8a0alwj">
            {[{
            id: 'all',
            label: 'All Active'
          }, {
            id: 'promotions',
            label: 'Sales & Deals'
          }, {
            id: 'discounts',
            label: 'Discount Codes'
          }, {
            id: 'donations',
            label: 'Anthology Donations'
          }, {
            id: 'expired',
            label: 'Expired'
          }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`} data-id="element-19827" data-mp="3ps9azmloiqx7">
                {tab.label}
              </button>)}
          </div>

          {/* Search */}
          <div className="relative w-full md:w-64" data-id="element-19828" data-mp="89y9ewnfisuq">
            <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${dk.textMuted}`} data-id="element-19829" data-mp="maiujzgth0re" />
            <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full pl-9 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 ${dk.input} ${rt.inputFocus}`} data-id="element-19830" data-mp="hch9xa3qstdt" />
          </div>
        </div>

        {/* Content Grid */}
        {filteredItems.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-19831" data-mp="13jblmv1aun9q">
            {filteredItems.map(item => <div key={item.id} className={`rounded-xl shadow-sm border overflow-hidden flex flex-col ${dk.card} ${item.status === 'active' ? item.type === 'anthology_donation' ? 'border-l-4 border-l-rose-500' : 'border-l-4 border-l-green-500' : item.status === 'scheduled' ? 'border-l-4 border-l-blue-500' : 'border-l-4 border-l-gray-400'}`} data-id="element-19832" data-mp="pqly2m64haai">
                <div className="p-5 flex-1 flex flex-col" data-id="element-19833" data-mp="6vnhxi1zix9p">
                  {/* Header: Status & Type */}
                  <div className="flex justify-between items-start mb-4" data-id="element-19834" data-mp="awz5zj57nafs">
                    <span className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium capitalize ${item.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : item.status === 'scheduled' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'}`} data-id="element-19835" data-mp="218d8pqx23qcv">
                      {item.status}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${item.type === 'promotion' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' : item.type === 'anthology_donation' ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300' : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'}`} data-id="element-19836" data-mp="11l0iyf3hcvg7">
                      {item.type === 'promotion' ? <>
                          <MegaphoneIcon className="w-3 h-3 mr-1" data-id="element-19837" data-mp="dxio2aox5h90" />
                          Promotion
                        </> : item.type === 'anthology_donation' ? <>
                          <HeartIcon className="w-3 h-3 mr-1" data-id="element-19838" data-mp="2pii0uyzx92xw" />
                          Donation
                        </> : <>
                          <TagIcon className="w-3 h-3 mr-1" data-id="element-19839" data-mp="2zh771uiggyyb" />
                          Code
                        </>}
                    </span>
                  </div>

                  {/* Title & Subtitle */}
                  <div className="mb-4" data-id="element-19840" data-mp="26tl7qyg9ggh9">
                    <h3 className={`font-bold text-lg leading-tight mb-1 ${dk.textHeading}`} data-id="element-19841" data-mp="26qgw5g0guigk">
                      {item.title}
                    </h3>
                    <p className={`text-sm ${dk.textMuted}`} data-id="element-19842" data-mp="2gkfstzhrta14">{item.subtitle}</p>
                  </div>

                  {/* Discount Badge / Code / Donation Info */}
                  <div className="mb-5" data-id="element-19843" data-mp="1o0r0yrssjuo4">
                    {item.type === 'discount_code' ? <div className={`inline-flex items-center px-3 py-1.5 rounded-lg font-mono font-bold text-sm border-dashed border-2 ${darkMode ? 'bg-gray-800 border-gray-600 text-gray-200' : 'bg-gray-50 border-gray-300 text-gray-700'}`} data-id="element-19844" data-mp="1g01icmo4fauc">
                        {item.details.code}
                        <button className="ml-2 hover:text-blue-500" title="Copy Code" data-id="element-19845" data-mp="1ku5oj3pkbeud">
                          <CopyIcon className="h-3 w-3" data-id="element-19846" data-mp="t27ztwenwjob" />
                        </button>
                      </div> : item.type === 'anthology_donation' ? <div className="flex flex-wrap gap-2" data-id="element-19847" data-mp="121bjb1hqr13c">
                        <div className={`inline-block px-3 py-1.5 rounded-lg font-bold text-sm ${darkMode ? 'bg-rose-900/30 text-rose-300' : 'bg-rose-100 text-rose-800'}`} data-id="element-19848" data-mp="2ifupux0sdspb">
                          {item.donationPercentage}% of Sales
                        </div>
                        <div className={`inline-block px-3 py-1.5 rounded-lg text-sm ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-19849" data-mp="47twkpb2ugwd">
                          {item.donationScope}
                        </div>
                      </div> : <div className={`inline-block px-3 py-1.5 rounded-lg font-bold text-sm ${darkMode ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-100 text-orange-800'}`} data-id="element-19850" data-mp="iy9iy7f8buw4">
                        {item.details.discountType === 'percentage' ? `${item.details.value}% OFF` : `$${item.details.value} OFF`}
                      </div>}
                  </div>

                  {/* Date Range */}
                  <div className="mt-auto" data-id="element-19851" data-mp="16nvhqtrow83l">
                    <div className={`flex items-center text-sm ${dk.textSecondary}`} data-id="element-19852" data-mp="32q77hmup6p2c">
                      <CalendarIcon className={`w-4 h-4 mr-2 ${dk.textMuted}`} data-id="element-19853" data-mp="2xy723evuz3yd" />
                      {formatDateRange(item.startDate, item.endDate)}
                    </div>
                  </div>
                </div>

                {/* Footer Stats & Actions */}
                <div className={`px-5 py-3 border-t flex items-center justify-between ${dk.divider} ${dk.surfaceMuted}`} data-id="element-19854" data-mp="3lnrwqv1djpnh">
                  <div className="flex items-center gap-4" data-id="element-19855" data-mp="3bplvmfne6njo">
                    {item.type === 'promotion' ? <>
                        <div className={`flex items-center text-xs ${dk.textMuted}`} title="Views" data-id="element-19856" data-mp="37oigxwh2yxk1">
                          <EyeIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-19857" data-mp="1w8tsf0o6hqh4" />
                          {item.stats.views?.toLocaleString()}
                        </div>
                        <div className={`flex items-center text-xs ${dk.textMuted}`} title="Clicks" data-id="element-19858" data-mp="fswelvdipwbo">
                          <MousePointerClickIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-19859" data-mp="kqft8we4ob45" />
                          {item.stats.clicks?.toLocaleString()}
                        </div>
                      </> : item.type === 'anthology_donation' ? <>
                        <div className={`flex items-center text-xs font-medium ${darkMode ? 'text-rose-400' : 'text-rose-600'}`} title="Total Donated" data-id="element-19860" data-mp="zj9l7hal6jpp">
                          <HeartIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-19861" data-mp="35ad8blo0lzeo" />
                          {item.totalDonated}
                        </div>
                        <div className={`flex items-center text-xs ${dk.textMuted}`} data-id="element-19862" data-mp="3a9zq88esg3tt">
                          {item.donationScope}
                        </div>
                      </> : <>
                        <div className={`flex items-center text-xs ${dk.textMuted}`} title="Uses" data-id="element-19863" data-mp="33zvue7pdvcgg">
                          <UsersIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-19864" data-mp="3cthd224o5dv5" />
                          {item.stats.uses?.toLocaleString()}
                        </div>
                        <div className={`flex items-center text-xs font-medium ${darkMode ? 'text-green-400' : 'text-green-600'}`} title="Revenue" data-id="element-19865" data-mp="3htvjxgujahrl">
                          {item.stats.revenue}
                        </div>
                      </>}
                  </div>

                  <div className="flex items-center gap-2" data-id="element-19866" data-mp="1xcqvx8ccusz8">
                    <button className={`p-1.5 rounded-md transition-colors hover:bg-gray-200 dark:hover:bg-gray-700 ${dk.textSecondary}`} title="Edit" data-id="element-19867" data-mp="ehmfrix8t39g">
                      <EditIcon className="w-4 h-4" data-id="element-19868" data-mp="5pu6vcnva26z" />
                    </button>
                    <button className="p-1.5 rounded-md transition-colors hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500" title="Delete" data-id="element-19869" data-mp="q7t9244mjxn">
                      <TrashIcon className="w-4 h-4" data-id="element-19870" data-mp="tfesk3bendjy" />
                    </button>
                  </div>
                </div>
              </div>)}
          </div> : <div className={`text-center py-20 rounded-xl border-2 border-dashed ${dk.border}`} data-id="element-19871" data-mp="jgz09x0n5y8v">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`} data-id="element-19872" data-mp="396ar6g5fsu8s">
              <FilterIcon className={`w-8 h-8 ${dk.textMuted}`} data-id="element-19873" data-mp="35n28b5xb0x75" />
            </div>
            <h3 className={`text-lg font-medium mb-1 ${dk.textHeading}`} data-id="element-19874" data-mp="3amohn4a0htao">
              No items found
            </h3>
            <p className={`text-sm mb-6 ${dk.textSecondary}`} data-id="element-19875" data-mp="2wnf7al8hltem">
              Try adjusting your filters or create a new promotion.
            </p>
            <Link to="/vendors/manage?type=sale" className={`inline-flex items-center px-4 py-2 rounded-lg font-medium text-white transition-colors ${rt.bgDark} hover:opacity-90`} data-id="element-19876" data-mp="31k5lf30qeexe">
              <PlusIcon className="w-5 h-5 mr-2" data-id="element-19877" data-mp="2dwe7juzjltu0" />
              Create New
            </Link>
          </div>}
      </div>
    </div>;
};
export default PromotionsDiscounts;